import axios from '../utils/axios'
import { toSnakeCase } from '../packages/utils/casing'
import { userMessageAtom, userMessageStore } from '../atoms/userMessageAtom'
import { AxiosError, AxiosResponse } from 'axios'
import { createStore } from 'jotai'
import { apiAtom } from './apiAtom'
import { UserPool } from '../contexts/CognitoContext'
import { CognitoUserSession } from 'amazon-cognito-identity-js'
import axiosInstance from '../utils/axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

const stateStore = createStore()

const refreshToken = async () => {
    console.log('Refreshing token...')
    if (stateStore.get(apiAtom).isRefreshingToken) return waitUntilTokenIsRefreshed()

    stateStore.set(apiAtom, {
        isRefreshingToken: true,
    })

    return new Promise((resolve, reject) => {
        const rejectWithStateUpdate = (err: Error) => {
            stateStore.set(apiAtom, () => ({
                isRefreshingToken: false,
            }))

            reject(err)
        }

        const cognitoUser = UserPool.getCurrentUser()
        if (!cognitoUser) {
            return rejectWithStateUpdate(new Error('No current user'))
        }

        cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
            if (err || !session) {
                return rejectWithStateUpdate(err || new Error('Failed to get cognito session.'))
            }

            const refreshToken = session.getRefreshToken()
            cognitoUser.refreshSession(refreshToken, (err: Error | null, session: CognitoUserSession | null) => {
                if (err || !session) {
                    rejectWithStateUpdate(err || new Error('Failed to refresh session.'))
                } else {
                    const newToken = session.getIdToken().getJwtToken()
                    localStorage.setItem('token_expiry', String(session.getIdToken().getExpiration() * 1000))
                    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`

                    stateStore.set(apiAtom, () => ({
                        isRefreshingToken: false,
                    }))
                    resolve(newToken)
                }
            })
        })
    })
}

const waitUntilTokenIsRefreshed = () =>
    new Promise<void>((resolve, reject) => {
        let unsubscribe: (() => void) | null = null

        const timeout = setTimeout(() => {
            if (unsubscribe) unsubscribe()

            reject(new Error('Timeout'))
        }, 3000)

        unsubscribe = stateStore.sub(apiAtom, () => {
            if (!stateStore.get(apiAtom).isRefreshingToken) {
                clearTimeout(timeout)
                if (unsubscribe) unsubscribe()
                resolve()
            }
        })
    })

interface ApiError extends Error {
    status?: number
    error?: Error
}

class ApiBase {
    #baseUrl = API_BASE_URL
    #methods = { GET: 'GET', POST: 'POST', PUT: 'PUT', DELETE: 'DELETE', PATCH: 'PATCH', HEAD: 'HEAD' }

    constructor() {
        if (!this.#baseUrl) {
            throw new Error('API_BASE_URL is not set in .env')
        }
        // make sure we don't instantiate this class directly
        if (new.target === ApiBase) {
            throw new TypeError('Cannot construct Abstract instances directly')
        }
    }

    _getRequest<T>(
        endpoint: string,
        { queryParameters }: { queryParameters?: Record<string, string | null | boolean> } = {},
    ) {
        return this._request(this.#methods.GET, endpoint, { queryParameters }) as Promise<T>
    }

    async _postRequest<T>(endpoint: string, data: Record<string, unknown> | null) {
        return this._request(this.#methods.POST, endpoint, { data }) as Promise<T>
    }

    async _putRequest(endpoint: string, data: Record<string, unknown> | null) {
        return this._request(this.#methods.PUT, endpoint, { data })
    }

    async _deleteRequest(endpoint: string) {
        const data = {}
        return this._request(this.#methods.DELETE, endpoint, { data })
    }

    async _patchRequest(endpoint: string, data: Record<string, unknown> | null, queryParameters = {}) {
        return this._request(this.#methods.PATCH, endpoint, { data, queryParameters })
    }

    async _headRequest(endpoint: string) {
        return this._requestHeaders(this.#methods.HEAD, endpoint)
    }

    /**
     * Make a request to the server.
     *
     * @param {*} method, e.g. 'GET', 'POST', 'PUT', 'DELETE'
     * @param {*} endpoint, e.g. '/password
     * @param {*} data payload for POST and PUT requests
     * @param {*} queryParameters query parameters to append to the endpoint
     * @returns response from the server
     */
    async _request(
        method: string,
        endpoint: string,
        {
            data = null,
            queryParameters = {},
        }: {
            data?: Record<string, unknown> | null
            queryParameters?: Record<string, string>
        } = {},
    ) {
        data = data ? toSnakeCase(data) : null
        queryParameters = toSnakeCase(queryParameters)
        const queryParametersString = new URLSearchParams(queryParameters).toString()
        if (queryParametersString) {
            endpoint += '?' + queryParametersString
        }

        try {
            const response = await this._requestWithAxios(method, this.#baseUrl + endpoint, data)

            return response
        } catch (err) {
            if (!(err instanceof Error)) {
                userMessageStore.set(userMessageAtom, {
                    type: 'error',
                    errorCode: null,
                    message: 'Ett okänt fel inträffade.',
                })
                throw err
            }

            if ('status' in err) {
                userMessageStore.set(userMessageAtom, {
                    type: 'error',
                    errorCode: err.status as number,
                    message: err.toString(),
                })
            }

            throw err
        }
    }

    /**
     * Make a request and only return the headers.
     */
    async _requestHeaders(
        method: string,
        endpoint: string,
        {
            data = null,
            queryParameters = {},
        }: { data?: Record<string, unknown> | null; queryParameters?: Record<string, string> } = {},
    ) {
        data = data ? toSnakeCase(data) : null
        queryParameters = toSnakeCase(queryParameters)
        const queryParametersString = new URLSearchParams(queryParameters).toString()
        if (queryParametersString) {
            endpoint += '?' + queryParametersString
        }
        return this._requestHeadersWithAxios(method, this.#baseUrl + endpoint, data)
    }

    /**
     * For now, this defers to the serverApi module. Once all calls have been migrated to
     * this class hierarchy, we can remove the serverApi module.
     *
     * @param {*} method http method, e.g. 'GET', 'POST', 'PUT', 'DELETE'
     * @param {*} url full url for the request
     * @param {*} data payload for POST and PUT requests
     * @returns response from the server
     */
    async _requestWithAxios(method: string, url: string, data: Record<string, unknown> | null = null) {
        const response = await this._requestBodyAndHeadersWithAxios(data, url, method)

        return response.data as unknown
    }

    /**
     * For now, this defers to the serverApi module. Once all calls have been migrated to
     * this class hierarchy, we can remove the serverApi module.
     */
    async _requestHeadersWithAxios(method: string, url: string, data: Record<string, unknown> | null = null) {
        const response = await this._requestBodyAndHeadersWithAxios(data, url, method)
        return response.headers
    }

    async _requestBodyAndHeadersWithAxios(
        data: Record<string, unknown> | null,
        url: string,
        method: string,
        isRetry = false,
    ): Promise<AxiosResponse> {
        const headers = {
            'Content-Type': 'application/json',
        }

        const { isRefreshingToken } = stateStore.get(apiAtom)

        if (isRefreshingToken) {
            try {
                await waitUntilTokenIsRefreshed()
            } catch (err) {
                console.error(err)
                window.location.reload()
            }
        }

        try {
            const response = await axios({ method, url, headers, data })
            return response
        } catch (error) {
            if (!(error instanceof Error)) {
                console.error(error)
                throw new Error('Unknown error')
            }

            if (!(error instanceof AxiosError)) {
                throw error
            }

            if (error.response) {
                if (error.response.status === 401) {
                    if (isRetry) {
                        window.location.reload()
                        throw new Error('Session expired')
                    }

                    try {
                        await refreshToken()

                        return this._requestBodyAndHeadersWithAxios(data, url, method, true)
                    } catch (err) {
                        console.error(err)
                        window.location.reload()
                        throw error
                    }
                }

                // Handle 4xx errors differently
                if (error.response.status >= 400 && error.response.status < 500) {
                    // Return or handle 400 errors as part of the normal flow
                    console.warn('Client error (4xx):', error.response.status, error.response.data)
                    const err: ApiError = new Error(error.response.data.message)
                    err.status = error.response.status
                    err.error = error

                    throw err
                }
                // For 5xx or other errors, throw an error
                const err: ApiError = new Error(`Server error! status: ${error.response.status}`)
                err.status = error.response.status

                throw err
            } else if (error.request) {
                throw new Error('No response received from server')
            } else {
                throw new Error(error.message)
            }
        }
    }

    get _methods() {
        return this.#methods
    }
}

export default ApiBase
